/**
 * Auto-corrects user input to a valid 24-hour time format (HH:MM)
 *
 * Examples:
 * - "1600" -> "16:00"
 * - "16" -> "16:00"
 * - "1" -> "01:00"
 * - "163" -> "16:30"
 * - "16:30" -> "16:30" (already formatted)
 * - "930" -> "09:30"
 * - "9" -> "09:00"
 */
export function autoCorrectTime(input: string): string {
  // Remove any non-digit characters except colon
  const cleaned = input.replace(/[^\d:]/g, '')

  // If already in HH:MM format and valid, return as is
  if (/^\d{2}:\d{2}$/.test(cleaned)) {
    const [hours, minutes] = cleaned.split(':').map(Number)
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return cleaned
    }
  }

  // Remove colon for processing
  const digitsOnly = cleaned.replace(':', '')

  // Handle empty or invalid input
  if (!digitsOnly || digitsOnly.length === 0) {
    return ''
  }

  let hours = 0
  let minutes = 0

  switch (digitsOnly.length) {
    case 1:
      // "1" -> "01:00"
      hours = parseInt(digitsOnly, 10)
      minutes = 0
      break

    case 2:
      // "16" -> "16:00"
      hours = parseInt(digitsOnly, 10)
      minutes = 0
      break

    case 3: {
      // "163" -> "16:30", "930" -> "09:30", "154" -> "15:40", "235" -> "23:50"
      // Check if first digit is 0-2 and interpret based on validity
      const firstDigit = parseInt(digitsOnly[0], 10)
      const firstTwoDigits = parseInt(digitsOnly.substring(0, 2), 10)
      const lastTwoDigits = parseInt(digitsOnly.substring(1), 10)
      const lastDigit = parseInt(digitsOnly[2], 10)

      // If interpreting as HH:M0 gives a valid hour (0-23), use that
      if (firstTwoDigits >= 0 && firstTwoDigits <= 23) {
        hours = firstTwoDigits
        minutes = lastDigit * 10
      } else {
        // Otherwise interpret as H:MM (for edge cases)
        hours = firstDigit
        minutes = lastTwoDigits
      }
      break
    }

    case 4:
    default:
      // "1600" -> "16:00"
      hours = parseInt(digitsOnly.substring(0, 2), 10)
      minutes = parseInt(digitsOnly.substring(2, 4), 10)
      break
  }

  // Validate and clamp values
  hours = Math.max(0, Math.min(23, hours))
  minutes = Math.max(0, Math.min(59, minutes))

  // Format with leading zeros
  const formattedHours = hours.toString().padStart(2, '0')
  const formattedMinutes = minutes.toString().padStart(2, '0')

  return `${formattedHours}:${formattedMinutes}`
}

export function formatDateTime(date: Date): string {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  const inputDate = date

  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const time = `${hours}:${minutes}`

  if (inputDate.getTime() === today.getTime()) {
    return `vandaag ${time}`
  } else if (inputDate.getTime() === yesterday.getTime()) {
    return `gisteren ${time}`
  } else {
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${day}-${month}-${year} ${time}`
  }
}

export function formatEndDateTime(startDate: Date, endDate: Date): string {
  // Check if start and end are on the same date
  const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())

  const hours = endDate.getHours().toString().padStart(2, '0')
  const minutes = endDate.getMinutes().toString().padStart(2, '0')

  if (startDateOnly.getTime() === endDateOnly.getTime()) {
    // Same date, show only time
    return `${hours}:${minutes}`
  } else {
    // Different dates, show full date and time
    const day = endDate.getDate().toString().padStart(2, '0')
    const month = (endDate.getMonth() + 1).toString().padStart(2, '0')
    const year = endDate.getFullYear()
    return `${day}-${month}-${year} ${hours}:${minutes}`
  }
}
